import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Switch } from "./ui/switch";
import { toast } from "sonner";
import { Bell, BellOff, Smartphone, Send, Info, Loader2 } from "lucide-react";
import {
  pushSupported,
  isIosSafari,
  isStandalone,
  getCurrentSubscription,
  subscribePush,
  unsubscribePush,
  sendTestPush,
} from "../lib/push";

/**
 * Notification preferences card. Lives inside Settings → Notifications tab.
 * Each browser/device subscribes individually (per-user opt-in), so the
 * toggle reflects the state on the *current* device only.
 */
export default function PushNotificationCard() {
  const [supported] = useState(() => pushSupported());
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [permission, setPermission] = useState(typeof Notification !== "undefined" ? Notification.permission : "default");

  useEffect(() => {
    if (!supported) return;
    (async () => {
      const sub = await getCurrentSubscription();
      setEnabled(!!sub);
    })();
  }, [supported]);

  const toggle = async (next) => {
    if (busy) return;
    setBusy(true);
    try {
      if (next) {
        await subscribePush();
        setEnabled(true);
        setPermission(Notification.permission);
        toast.success("Notifications enabled on this device");
      } else {
        await unsubscribePush();
        setEnabled(false);
        toast.success("Notifications disabled on this device");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to update notifications");
    } finally {
      setBusy(false);
    }
  };

  const doTest = async () => {
    setSendingTest(true);
    try {
      const res = await sendTestPush();
      if ((res?.sent ?? 0) > 0) toast.success(`Test sent to ${res.sent} device${res.sent === 1 ? "" : "s"}`);
      else toast.error("No subscribed devices to send to");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send test");
    } finally {
      setSendingTest(false);
    }
  };

  if (!supported) {
    return (
      <Card data-testid="push-card-unsupported">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><BellOff className="w-5 h-5" /> Push notifications</CardTitle>
          <CardDescription>This browser doesn&apos;t support web push notifications.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const iosBlocked = isIosSafari() && !isStandalone();

  return (
    <Card data-testid="push-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Bell className="w-5 h-5 text-[#0F4C81]" /> Push notifications</CardTitle>
        <CardDescription>
          Get instant alerts on this device for new leads, payments, signed quotes, replies, and task assignments.
          Each device subscribes independently — turn this on once per browser or phone you want notified.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {iosBlocked && (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-800 flex items-start gap-2" data-testid="push-ios-hint">
            <Smartphone className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold">iPhone / iPad setup</p>
              <p>Tap the <b>Share</b> icon in Safari → <b>Add to Home Screen</b>, then open Tides Hub from the new icon. Notifications can only be enabled inside the home-screen app.</p>
            </div>
          </div>
        )}
        {permission === "denied" && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3 text-xs text-red-800 flex items-start gap-2">
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              Notifications are blocked for this site. Open your browser&apos;s site settings and switch notifications to <b>Allow</b>, then refresh.
            </div>
          </div>
        )}
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-slate-800">Enable on this device</p>
            <p className="text-xs text-slate-500">
              {enabled ? "Notifications are active for this browser." : "Notifications are off for this browser."}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {busy && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
            <Switch
              checked={enabled}
              onCheckedChange={toggle}
              disabled={busy || iosBlocked || permission === "denied"}
              data-testid="push-toggle"
            />
          </div>
        </div>

        {enabled && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={doTest}
            disabled={sendingTest}
            className="border-[#0F4C81]/30 text-[#0F4C81] hover:bg-[#0F4C81]/10"
            data-testid="push-test-btn"
          >
            <Send className="w-3.5 h-3.5 mr-1.5" />
            {sendingTest ? "Sending…" : "Send test notification"}
          </Button>
        )}

        <div className="text-[11px] text-slate-400 border-t pt-3">
          You&apos;ll be notified for: new leads (website / referrals / manual contact adds), invoice payments &amp; full payoffs, customer-signed quotes, email engagement (clicks / bounces / replies), and tasks assigned to you.
        </div>
      </CardContent>
    </Card>
  );
}
